import { Link } from "react-router-dom"
import { ShieldAlert } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useCurrentOrg, orgPath } from "@/hooks/use-current-org"
import { requiredRightLabel } from "@/lib/error-messages"
import type { PermissionModule, PermissionOperation } from "@/lib/permissions"

const MODULE_LABELS: Record<PermissionModule, string> = {
  training: "Formation",
  commercial: "Commercial",
  crm: "CRM",
  hr: "Ressources humaines",
  qualiopi: "Qualiopi",
  jury: "Jurys",
  documents: "Documents",
  templates: "Modèles",
  messaging: "Messagerie",
  admin: "Administration",
  reports: "Rapports",
  settings: "Paramètres",
}

/**
 * Rendered in place of a module the current user is not allowed to open (via
 * `PermissionRoute`), instead of letting the page mount and fire API calls that
 * would 403. Clean, accessible dead-end with a way back — a deep-link to a
 * forbidden area (shared by email, chat, an old bookmark) lands here rather than
 * on a white screen or a raw error.
 *
 * @example <ForbiddenPage module="crm" operation="delete" /> // « … le droit CRM › Supprimer. »
 */
export function ForbiddenPage({ module, operation = "read" }: { module: PermissionModule; operation?: PermissionOperation }) {
  const slug = useCurrentOrg()
  // Action refusée à un lecteur du module : droit nommé.
  const refusal =
    operation === "read"
      ? `Votre rôle ne vous donne pas accès au module « ${MODULE_LABELS[module]} ».`
      : `Votre rôle ne vous donne pas le droit ${requiredRightLabel(module, operation)}.`

  return (
    <div
      className="flex flex-col items-center justify-center py-24 px-4 text-center"
      data-test="forbidden-page"
      role="alert"
    >
      <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-amber-500/10">
        <ShieldAlert className="h-8 w-8 text-amber-500" />
      </div>
      <h1 className="mb-2 text-2xl font-semibold">Accès non autorisé</h1>
      <p className="mb-8 max-w-md text-sm text-muted-foreground" data-test="forbidden-reason">
        {refusal} Si vous pensez qu'il s'agit d'une erreur, contactez l'administrateur de votre organisme.
      </p>
      <Button asChild data-test="forbidden-back-dashboard">
        <Link to={orgPath(slug, "/dashboard")}>Retour au tableau de bord</Link>
      </Button>
    </div>
  )
}

export default ForbiddenPage
